/**
 * @file Phone auth action.
 */

import { isValidPhone, parsePhone } from '../util/phone';
import getLogger from '../util/logger';
import { getErrorString } from '../util/normalize-errors';

const log = getLogger('PhoneAuthAction');

export const PHONE_AUTH_SUBMIT_REQUEST = 'auth/PHONE_SUBMIT_REQUEST';
export const PHONE_AUTH_SUBMIT_SUCCESS = 'auth/PHONE_SUBMIT_SUCCESS';
export const PHONE_AUTH_SUBMIT_FAILURE = 'auth/PHONE_SUBMIT_FAILURE';

export const PHONE_AUTH_VERIFY_REQUEST = 'auth/PHONE_VERIFY_REQUEST';
export const PHONE_AUTH_VERIFY_SUCCESS = 'auth/PHONE_VERIFY_SUCCESS';
export const PHONE_AUTH_VERIFY_FAILURE = 'auth/PHONE_VERIFY_FAILURE';

export const PHONE_AUTH_CODE_RESEND_REQUEST =
  'auth/PHONE_AUTH_CODE_RESEND_REQUEST';
export const PHONE_AUTH_CODE_RESEND_SUCCESS =
  'auth/PHONE_AUTH_CODE_RESEND_SUCCESS';
export const PHONE_AUTH_CODE_RESEND_FAILURE =
  'auth/PHONE_AUTH_CODE_RESEND_FAILURE';

const phoneAuthSubmitRequest = () => {
  return { type: PHONE_AUTH_SUBMIT_REQUEST };
};

const phoneAuthSubmitSuccess = () => {
  return { type: PHONE_AUTH_SUBMIT_SUCCESS };
};

const phoneAuthSubmitFailure = error => {
  return { type: PHONE_AUTH_SUBMIT_FAILURE, error };
};

const phoneAuthVerifyRequest = () => {
  return { type: PHONE_AUTH_VERIFY_REQUEST };
};

const phoneAuthVerifySuccess = () => {
  return { type: PHONE_AUTH_VERIFY_SUCCESS };
};

const phoneAuthVerifyFailure = error => {
  return { type: PHONE_AUTH_VERIFY_FAILURE, error };
};

const phoneAuthCodeResendRequest = () => {
  return { type: PHONE_AUTH_CODE_RESEND_REQUEST };
};

const phoneAuthCodeResendSuccess = () => {
  return { type: PHONE_AUTH_CODE_RESEND_SUCCESS };
};

const phoneAuthCodeResendFailure = error => {
  return { type: PHONE_AUTH_CODE_RESEND_FAILURE, error };
};

export const phoneAuthSubmit = (
  phoneVerificationRequest,
  phone,
  rethrow,
) => async dispatch => {
  log.info('phoneAuthSubmit() :: Requesting phone auth for:', phone);

  dispatch(phoneAuthSubmitRequest());

  const parsedPhone = parsePhone(phone);

  if (!isValidPhone(parsedPhone)) {
    dispatch(phoneAuthSubmitFailure('Invalid phone number'));

    if (rethrow) {
      throw Error('Invalid phone number');
    }

    return;
  }

  try {
    await phoneVerificationRequest(parsedPhone);

    dispatch(phoneAuthSubmitSuccess());

    log.info('phoneAuthSubmit() :: Success');
  } catch (error) {
    log.error('phoneAuthSubmit() :: Error:', error);

    dispatch(phoneAuthSubmitFailure(getErrorString(error)));

    const message = 'An unexpected error has occurred, please try again';
    if (rethrow) {
      throw Error(message);
    }
  }
};

export const phoneAuthVerify = (
  accountLoginByPhone,
  phone,
  code,
  onLogin,
) => async dispatch => {
  log.info('phoneAuthVerify() :: Phone auth verification...');

  dispatch(phoneAuthVerifyRequest());

  if (code.length < 4) {
    dispatch(phoneAuthVerifyFailure('Invalid code'));
    return;
  }

  try {
    await accountLoginByPhone(phone, code);

    dispatch(phoneAuthVerifySuccess());

    log.info('phoneAuthVerify() :: Success');

    if (onLogin) {
      onLogin();
    }
  } catch (error) {
    log.error('phoneAuthVerify() :: Error:', error);

    dispatch(phoneAuthVerifyFailure(getErrorString(error)));
  }
};

export const resendPhoneAuthCode = (
  phoneVerificationRequest,
  phone,
) => async dispatch => {
  log.info('resendPhoneAuthCode() :: Resending phone auth code for:', phone);

  dispatch(phoneAuthCodeResendRequest());

  const parsedPhone = parsePhone(phone);

  if (!isValidPhone(parsedPhone)) {
    dispatch(phoneAuthCodeResendFailure('Invalid phone number'));
    return;
  }

  try {
    await phoneVerificationRequest(parsedPhone);

    dispatch(phoneAuthCodeResendSuccess());

    log.info('resendPhoneAuthCode() :: Success');
  } catch (error) {
    log.error('resendPhoneAuthCode() :: Error:', error);

    dispatch(phoneAuthCodeResendFailure(getErrorString(error)));
  }
};
